import dayjs from 'dayjs';
import Datepicker from 'react-tailwindcss-datepicker';
import React, { PureComponent, useEffect, useState } from 'react';
import { Sector, PieChart, Pie, ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line } from 'recharts';
import { fetchClient } from '../../util/fetchClient.js';
import RoenBranchSelector from '../_Shared/RoenBranchSelector.js';
import { getStartOfQuarter, getStartOfHalfYear } from '../../util/date.js';

const DEFAULT_BRANCH = { id: 'all', name: 'Todas' };

const fetchBranches = async () => {
  const { body: branches } = await fetchClient('/v1/branches');
  const data = branches?.data || [];

  return [
    DEFAULT_BRANCH,
    ...data,
  ];
};

const fetchReports = async ({ startTime, endTime, branchId }) => {
  const eod = dayjs().endOf('day');
  const startOfMonth = dayjs().startOf('month');
  const start = (startTime || startOfMonth).toISOString();
  const end = (endTime || eod).toISOString();
  const { body: reports } = await fetchClient(`/v1/reports?start_time=${start}&end_time=${end}&branch_id=${branchId}`);
  return reports || {};
};

const toMoney = amount => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'mxn' }).format(amount || 0);

const formatDate = date => dayjs(date).format('DD/MM/YYYY');

class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-40)">
          {payload.value}
        </text>
      </g>
    );
  }
}

export default function Reports() {
  const eod = dayjs().endOf('day');
  const startOfWeek = dayjs().startOf('week');
  const startOfMonth = dayjs().startOf('month');
  const startOfQuarter = dayjs(getStartOfQuarter(new Date()));
  const startOfHalfYear = dayjs(getStartOfHalfYear(new Date()));
  const startOfYear = dayjs().startOf('year');
  const [datesSelected, setDatesSelected] = useState({ startDate: startOfMonth, endDate: eod });
  const [branches, setBranches] = useState([]);
  const [branchSelected, setBranchSelected] = useState(DEFAULT_BRANCH);

  const [incomeActiveIndex, setIncomeActiveIndex] = useState(0);
  const [expensesActiveIndex, setExpensesActiveIndex] = useState(0);

  const handleDateChange = (newDates) => {
    setDatesSelected({
      startDate: dayjs(newDates.startDate).startOf('day'),
      endDate: dayjs(newDates.endDate).endOf('day'),
    });
  };

  const [reports, setReports] = useState({
    // Hard stats
    grossProfitReport: {},
    operatingProfitReport: {},
    netProfitReport: {},

    // Pie charts
    incomeReport: {},
    expensesReport: {},

    // Histograms
    incomeVsExpensesReport: []
  });

  useEffect(() => {
    async function getBranches() {
      if (!ignore) {
        const branches = await fetchBranches();
        setBranches(branches);
      }
    }

    let ignore = false;
    getBranches();
    return () => {
      ignore = true;
    }
  }, []);

  useEffect(() => {
    async function getReports() {
      if (!ignore) {
        const reports = await fetchReports({ startTime: datesSelected.startDate && new Date(datesSelected.startDate), endTime: datesSelected.endDate && new Date(datesSelected.endDate), branchId: branchSelected.id });
        setReports(reports);
      }
    }

    let ignore = false;
    getReports();
    return () => {
      ignore = true;
    }
  }, [datesSelected, branchSelected]);

  const renderActiveShape = (props) => {
    const { cx, cy, midAngle, name, innerRadius, outerRadius, startAngle, endAngle, fill, percent, value } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 5) * cos;
    const sy = cy + (outerRadius + 5) * sin;
    const mx = cx + (outerRadius + 15) * cos;
    const my = cy + (outerRadius + 15) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 15;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 1}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#000000" fill="none" />
        <circle cx={ex} cy={ey} r={2} fill="#000000" stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 10} y={ey} textAnchor={textAnchor} fill="#333" className="text-sm">
          {name}
        </text>
        <text x={ex + (cos >= 0 ? 1 : -1) * 10} y={ey} dy={18} textAnchor={textAnchor} fill="#999" className="text-sm">
          {toMoney(value)} {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  const renderAmountToMoneyYAxis = amount => toMoney(amount);

  const renderTranslatedLegend = (value, entry) => {
    const { color } = entry;
    const translatedValue = value === 'income' ? 'Ingresos' : 'Egresos';
    return <span style={{ color }}>{translatedValue}</span>;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p style={{ color: payload[0].color }}>{`${payload[0].name === 'income' ? 'Ingresos' : 'Egresos'}: ${toMoney(payload[0].value)}`}</p>
          <p style={{ color: payload[1].color }}>{`${payload[1].name === 'income' ? 'Ingresos' : 'Egresos'}: ${toMoney(payload[1].value)}`}</p>
          <p>Fecha: {label}</p>
        </div>
      );
    }
  
    return null;
  };

  const onIncomePieEnter = (_, index) => {
    setIncomeActiveIndex(index);
  };

  const onExpensesPieEnter = (_, index) => {
    setExpensesActiveIndex(index);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <header>
        <div className="mx-auto flex flex-wrap items-center gap-6">
          <h1 className="text-base font-semibold leading-7 text-gray-900">Sucursal</h1>
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto -ml-3">
            <RoenBranchSelector
              branchOptions={branches}
              branchSelected={branchSelected}
              onChange={setBranchSelected}
            />
          </div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">Periodo</h1>
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto -ml-3">
            <Datepicker
              i18n={"es-mx"}
              inputClassName={"relative transition-all duration-300 py-1.5 pl-4 pr-10 w-full min-w-72 max-w-72 border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-lg tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-indigo-500 focus:ring-indigo-500/20"}
              separator={"-"}
              primaryColor={"indigo"}
              useRange={true}
              displayFormat={"DD/MM/YYYY"}
              placeholder={`${formatDate(startOfMonth)} - ${formatDate(eod)}`}
              value={datesSelected}
              showShortcuts={true}
              onChange={handleDateChange}
              configs={{
                shortcuts: {
                  thisWeek: {
                    text: "Esta semana",
                    period: {
                      start: startOfWeek,
                      end: eod
                    },
                  },
                  thisMonth: {
                    text: "Este mes",
                    period: {
                      start: startOfMonth,
                      end: eod
                    },
                  },
                  thisQuarter: {
                    text: "Este trimestre",
                    period: {
                      start: startOfQuarter,
                      end: eod
                    },
                  },
                  thisHalfYear: {
                    text: "Este semestre",
                    period: {
                      start: startOfHalfYear,
                      end: eod
                    },
                  },
                  thisYear: {
                    text: "Este año",
                    period: {
                      start: startOfYear,
                      end: eod
                    },
                  },
                }
              }}
            />
          </div>
        </div>
      </header>

      <div className="">
        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3">
          <div key="income" className="overflow-hidden bg-white px-4 py-5 shadow">
            <dt className="truncate text-sm font-medium text-gray-500">Ingresos Totales</dt>
            <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 py-3">
              {toMoney(reports.incomeReport && reports.incomeReport.amount)}
            </dd>
          </div>
          <div key="expenses" className="overflow-hidden bg-white px-4 py-5 shadow">
            <dt className="truncate text-sm font-medium text-gray-500">Egresos Totales</dt>
            <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 py-3">
              {toMoney(reports.expensesReport && reports.expensesReport.amount)}
            </dd>
          </div>
          <div key="net-profit" className="overflow-hidden bg-white px-4 py-5 shadow">
            <dt className="truncate text-sm font-medium text-gray-500">Utilidad Neta</dt>
            <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 py-3">
              {toMoney(reports.netProfitReport && reports.netProfitReport.amount)}
              &nbsp;
              <span className="text-xl text-gray-500 font-medium">
                ({reports.netProfitReport && reports.netProfitReport.percentage}%)
              </span>
            </dd>
          </div>
        </dl>
      </div>
      <div className="mt-3 flow-root">
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="overflow-hidden bg-white px-4 py-5 shadow">
            <dt className="truncate text-base font-medium font-semibold text-gray-700">Detalle de Ingresos</dt>
            <div className="-mt-10">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={300}>
                <PieChart width="60%" height="60%">
                  {
                    reports.incomeReport && reports.incomeReport.subcategories && <Pie
                      activeIndex={incomeActiveIndex}
                      activeShape={renderActiveShape}
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={600}
                      data={reports.incomeReport.subcategories.filter(subcategory => subcategory.amount)}
                      nameKey="name"
                      dataKey="amount"
                      legendType="diamond"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      paddingAngle={0}
                      fill="#22c55e"
                      onMouseEnter={onIncomePieEnter}
                    />
                  }
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="overflow-hidden bg-white px-4 py-5 shadow">
            <dt className="truncate text-base font-medium font-semibold text-gray-700">Detalle de Egresos</dt>
            <div className="-mt-10">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={300}>
                <PieChart width="60%" height="60%">
                  {
                    reports.expensesReport && reports.expensesReport.subcategories && <Pie
                      activeIndex={expensesActiveIndex}
                      activeShape={renderActiveShape}
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={600}
                      data={reports.expensesReport.subcategories.filter(subcategory => subcategory.amount)}
                      nameKey="name"
                      dataKey="amount"
                      legendType="diamond"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      paddingAngle={0}
                      fill="#f87171"
                      onMouseEnter={onExpensesPieEnter}
                    />
                  }
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 flow-root">
        <div className="grid grid-cols-1">
          <div className="overflow-hidden bg-white px-4 py-5 shadow">
            <div>
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={300}>
                {
                  reports.incomeVsExpensesReport && <LineChart
                    width="60%"
                    height="40%"
                    data={reports.incomeVsExpensesReport}
                    margin={{
                      top: 0,
                      right: 30,
                      left: 50,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="1 1" />
                    <XAxis
                      dataKey="date"
                      tick={<CustomizedAxisTick />}
                    />
                    <YAxis
                      tickFormatter={renderAmountToMoneyYAxis}
                    />
                    <Tooltip
                      cursor={false}
                      content={CustomTooltip}
                    />
                    <Legend
                      align="center"
                      verticalAlign="top"
                      height={50}
                      iconType="line"
                      formatter={renderTranslatedLegend}
                    />
                    <Line
                      type="monotone"
                      dataKey="income"
                      stroke="#22c55e"
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={600}
                    />
                    <Line
                      type="monotone"
                      dataKey="expenses"
                      stroke="#f87171"
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={600}
                    />
                  </LineChart>
                }
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
